import styles from './WidgetGroupContent.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import { produce } from 'immer';
import { cloneDeep, partition } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Layout } from 'react-grid-layout';
import { useTranslation } from 'react-i18next';

import { GridLayout } from '@work4all/components/lib/dataDisplay/grid-layout/GridLayout';
import {
  LayoutResponsiveBreakpoint,
  LayoutResponsiveDefinition,
} from '@work4all/components/lib/utils/react-grid-layout/react-grid-layout-models';

import { FileViewMode, useWidgetsDataBag } from '@work4all/data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import {
  ILayoutDefinition,
  LayoutDefinitionTypes,
} from '@work4all/models/lib/Layout';

import { Breakpoint } from '@work4all/utils/lib/variables';

import { settings, useSetting } from '../../../../../../settings';
import { resolvePreviewType } from '../../../FilePage';
import { FileContext } from '../../FileContext';
import { useFileWidgetSettings } from '../../widget-settings-dialog/use-file-widget-settings';
import { CardWidgetContainer } from '../CardWidgetContainer';
import { useWidgetDragState } from '../hooks/use-widget-drag-state';

export type IWidgetGroupContentProps = {
  widgets: ILayoutDefinition[];
  viewMode: FileViewMode;
  groupId: string;
  convertibleEntities?: Entities[];
  collapsed?: boolean;
};

export const LAYOUT_SIZES: LayoutResponsiveBreakpoint[] = [
  'lg',
  'md',
  'sm',
  'xs',
  'xxs',
];

export const WIDGET_COLS = { lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 };
const DEFAULT_WIDGET_WIDTHS = { lg: 4, md: 4, sm: 4, xs: 6, xxs: 12 };
const DEFAULT_WIDGET_HEIGHTS = { lg: 6, md: 6, sm: 6, xs: 6, xxs: 6 };

export const BLANK_LAYOUT: LayoutResponsiveDefinition = {
  lg: [],
  md: [],
  sm: [],
  xs: [],
  xxs: [],
};

export function WidgetGroupContent({
  widgets: definitions,
  viewMode,
  groupId,
  convertibleEntities,
  collapsed,
}: IWidgetGroupContentProps) {
  const isViewportDesktop = useMediaQuery<Theme>(
    (theme) => theme.breakpoints.up('md'),
    { noSsr: true }
  );

  const { t } = useTranslation();

  const { widgetsData, currentBreakpoint, setCurrentBreakpoint, fetchMore } =
    useWidgetsDataBag();

  const fileContext = useContext(FileContext);

  const { setDraggedWidgetId } = useWidgetDragState();

  const { persistedFileWidgetLayouts, collapsedWidgets, hiddenWidgets } =
    useFileWidgetSettings(fileContext.type);

  // Group widgets by "collapsed" state on above "md" breakpoint. On smaller
  // screens just return all widgets as one group.

  const widgetGroups = useMemo(() => {
    const widgets = definitions
      .map((definition) => {
        const widgetData = widgetsData[definition.id];
        const data = widgetData
          ? {
              ...widgetData,
              widgetTitle: t(widgetData.widgetTitle ?? definition.title),
              // Ensure compatibility with CardWidgetContainer
              totalCount: widgetData.totalCount ?? 0,
              data: widgetData.data ?? [],
            }
          : {
              data: [],
              totalCount: 0,
              widgetId: definition.id,
              widgetTitle: t(definition.title),
            };

        return {
          definition,
          data,
          collapsed:
            isViewportDesktop &&
            (collapsedWidgets.value[definition.id] ?? false),
          hidden:
            isViewportDesktop && hiddenWidgets.value[definition.id] === true,
        };
      })

      .sort((a, b) => {
        return a.data.widgetTitle.localeCompare(b.data.widgetTitle);
      });

    if (!isViewportDesktop) {
      return [widgets];
    }

    const result = partition(
      widgets,
      (widget) => viewMode !== 'individual' && !widget.collapsed
    );

    return result;
  }, [
    definitions,
    isViewportDesktop,
    widgetsData,
    t,
    collapsedWidgets.value,
    hiddenWidgets.value,
    viewMode,
  ]);

  const [widgetGroupLayouts, setWidgetGroupLayouts] = useState<
    Array<LayoutResponsiveDefinition>
  >([]);

  useEffect(() => {
    const resultingLayouts = produce(
      persistedFileWidgetLayouts.value.layouts,
      (savedLayouts) => {
        while (savedLayouts.length < widgetGroups.length) {
          savedLayouts.push(produce(BLANK_LAYOUT, (draft) => draft));
        }

        for (let layoutIdx = 0; layoutIdx < savedLayouts.length; layoutIdx++) {
          const layout = produce(savedLayouts[layoutIdx], (draft) => {
            for (const layoutSize in draft) {
              draft[layoutSize] = draft[layoutSize].filter(
                (x) => !hiddenWidgets.value[x.i]
              );
            }

            return draft;
          });

          const widgetIds =
            widgetGroups[layoutIdx]
              ?.map((w) => w.definition.id)
              .filter((id) => !hiddenWidgets.value[id]) || [];

          for (let i = 0; i < widgetIds.length; i++) {
            const widgetId = widgetIds[i];
            for (const layoutSize in layout) {
              const widget = layout[layoutSize]?.find((x) => x.i === widgetId);
              if (!widget) {
                const width = DEFAULT_WIDGET_WIDTHS[layoutSize];
                const height = DEFAULT_WIDGET_HEIGHTS[layoutSize];
                const columns = WIDGET_COLS[layoutSize];

                layout[layoutSize].push({
                  i: widgetId,
                  h: height,
                  w: width,
                  x: (i * width) % columns,
                  y: Infinity,
                  minW: 2,
                });
              }
            }
          }

          savedLayouts[layoutIdx] = layout;
        }
        return savedLayouts;
      }
    );

    setWidgetGroupLayouts(resultingLayouts);
  }, [
    hiddenWidgets.value,
    persistedFileWidgetLayouts.value.layouts,
    viewMode,
    widgetGroups,
  ]);

  const setLayout = useCallback(
    (updatedLayouts: Layout[], groupIndex: number) => {
      const newLayouts = [...widgetGroupLayouts];

      const currentBreakpointLayouts: Layout[] =
        newLayouts[groupIndex][currentBreakpoint];

      const updatedBreakpointLayouts = currentBreakpointLayouts.map(
        (layout) => {
          const updatedLayout = updatedLayouts.find(
            (_layout) => layout.i === _layout.i
          );

          if (updatedLayout) {
            const isCollapsed = collapsedWidgets.value[layout.i] ?? false;
            const h = isCollapsed ? layout.h : updatedLayout.h;

            return { ...updatedLayout, h };
          }

          return layout;
        }
      );

      newLayouts[groupIndex] = {
        ...newLayouts[groupIndex],
        [currentBreakpoint]: updatedBreakpointLayouts,
      };
      persistedFileWidgetLayouts.set({ layouts: newLayouts });
    },
    [
      currentBreakpoint,
      persistedFileWidgetLayouts,
      widgetGroupLayouts,
      collapsedWidgets.value,
    ]
  );

  const isPhone = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'));

  // Render widgets for a group
  const renderWidgets = useCallback(
    (widgets) => {
      return widgets.map(({ definition, data, collapsed, hidden }) => {
        if (hidden) return null;

        switch (definition.type) {
          case LayoutDefinitionTypes.EntityList:
            return (
              <div
                key={definition.id}
                onMouseDown={() => setDraggedWidgetId(definition.id)}
                draggable
              >
                <CardWidgetContainer<unknown>
                  definition={definition}
                  title={data.widgetTitle}
                  items={data.data}
                  totalCount={data.totalCount}
                  fetchMore={fetchMore(definition.id)}
                  collapsible={isDesktop}
                  collapsed={collapsed}
                  onCollapsedChange={() => {
                    collapsedWidgets.set({
                      ...collapsedWidgets.value,
                      [definition.id]: !collapsed,
                    });
                  }}
                  convertibleEntities={convertibleEntities}
                />
              </div>
            );
          default:
            return null;
        }
      });
    },
    [
      isDesktop,
      convertibleEntities,
      fetchMore,
      setDraggedWidgetId,
      collapsedWidgets,
    ]
  );

  return (
    <>
      {widgetGroups.map((widgets, groupIndex) => {
        if (isViewportDesktop && groupIndex === 0) {
          return null;
        }

        let layout: LayoutResponsiveDefinition = cloneDeep(
          widgetGroupLayouts[groupIndex]
        );

        if (layout)
          for (const size of LAYOUT_SIZES) {
            layout = {
              ...layout,
              [size]: layout[size].map((lay) => {
                const widget = widgets.find((x) => x.definition.id === lay.i);
                const resolvedHeight = widget?.collapsed
                  ? 2
                  : !widget?.collapsed && lay.h === 1
                  ? 3
                  : lay.h;
                return {
                  ...lay,
                  y: lay.y,
                  h: resolvedHeight,
                  isResizable: !widget?.collapsed,
                } as Layout;
              }),
            };
          }

        return !isPhone && viewMode === 'individual' ? (
          <DroppableWidgetGroupContent
            key={groupIndex}
            groupId={groupId}
            widgets={widgets}
            renderWidgets={renderWidgets}
            groupIndex={groupIndex}
            layout={layout}
            viewMode={viewMode}
            setLayout={setLayout}
            setCurrentBreakpoint={setCurrentBreakpoint}
            collapsed={collapsed}
          />
        ) : (
          <div key={groupIndex} className={styles.widgets}>
            {renderWidgets(widgets)}
          </div>
        );
      })}
    </>
  );
}

export const DroppableWidgetGroupContent = (props: {
  widgets;
  renderWidgets: (widgets: any, fixedHeight?: boolean) => any;
  groupId: string;
  groupIndex: number;
  layout: LayoutResponsiveDefinition;
  viewMode: 'individual';
  setLayout: (updatedLayouts: Layout[], groupIndex: number) => void;
  setCurrentBreakpoint: (value: Breakpoint) => void;
  collapsed?: boolean;
}) => {
  const {
    widgets,
    renderWidgets,
    groupId,
    groupIndex,
    layout,
    viewMode,
    setLayout,
    setCurrentBreakpoint,
    collapsed,
  } = props;
  const { draggedWidgetId, setDraggedWidgetId } = useWidgetDragState();

  const fileContext = useContext(FileContext);
  const { filePageWidgetGroupRegister } = useFileWidgetSettings(
    fileContext.type
  );

  const previewSize = useSetting(
    settings.filePreviewSize({
      fileType: resolvePreviewType(fileContext.type),
    })
  );

  const [gridHovered, setGridHovered] = useState(false);

  const groupIsDroppable =
    draggedWidgetId &&
    !widgets.find((x) => x.definition.id === draggedWidgetId);

  const containerRef = useRef<HTMLDivElement>(null);
  const gridHoveredRef = useRef(false);

  useEffect(() => {
    if (collapsed) {
      return;
    }

    const mouseUp = () => {
      if (groupIsDroppable && gridHoveredRef.current) {
        filePageWidgetGroupRegister.set({
          ...filePageWidgetGroupRegister.value,
          [draggedWidgetId]: groupId,
        });
      }
      setDraggedWidgetId(null);
    };

    const mouseMove = (event) => {
      const pos = containerRef.current?.getBoundingClientRect();

      if (!pos) {
        return;
      }

      setGridHovered(false);
      gridHoveredRef.current = false;
      if (event.clientX >= pos.left && event.clientX <= pos.right) {
        if (event.clientY >= pos.top && event.clientY <= pos.bottom) {
          setGridHovered(true);
          gridHoveredRef.current = true;
        }
      }

      document.addEventListener('mouseup', mouseUp);
    };

    document.addEventListener('mousemove', mouseMove);

    return () => {
      setTimeout(() => {
        document.removeEventListener('mousemove', mouseMove);
        document.removeEventListener('mouseup', mouseUp);
      });
    };
  }, [
    draggedWidgetId,
    filePageWidgetGroupRegister,
    groupId,
    groupIsDroppable,
    setDraggedWidgetId,
    collapsed,
  ]);

  return (
    <div className={styles.gridContainer} ref={containerRef}>
      <GridLayout
        key={groupIndex}
        sizeDeps={[previewSize.value]}
        layouts={layout}
        cols={WIDGET_COLS}
        isDraggable={viewMode === 'individual'}
        isResizable={viewMode === 'individual'}
        onDragStop={(layout) => {
          if (gridHovered) {
            setLayout(layout, groupIndex);
          }
        }}
        onResizeStop={(layout) => {
          setLayout(layout, groupIndex);
        }}
        onBreakpointChange={setCurrentBreakpoint}
        style={{ marginTop: '-0.5rem' }}
        onDragStart={(_, oldItem) => {
          setDraggedWidgetId(oldItem.i);
        }}
      >
        {renderWidgets(widgets)}
      </GridLayout>

      {groupIsDroppable && gridHovered && (
        <div className={styles.gridDropOverlay}></div>
      )}
    </div>
  );
};
